import { muteSaltclickEndSignals } from './saltclickEndSignals';

/**
 * Which account the widget currently holds a verified session for. Kept on the
 * body next to the widget's visibility classes, for the same reason: the widget
 * is init'd once per page load and outlives the React tree that booted it, so a
 * ref would forget who was chatting and the next login in the same tab would
 * inherit their conversation — the bug verification exists to close
 * (GOTU-5243 / GOTU-5918). Its lifetime matches the widget's: both are torn down
 * only when the document is.
 */
const VERIFIED_FOR_KEY = 'saltclickVerifiedFor';

export const getSaltclickVerifiedForUserId = (): string | undefined =>
  document.body.dataset[VERIFIED_FOR_KEY];

export const setSaltclickVerifiedForUserId = (userId?: string): void => {
  if (userId) {
    document.body.dataset[VERIFIED_FOR_KEY] = userId;

    return;
  }
  delete document.body.dataset[VERIFIED_FOR_KEY];
};

/**
 * `clearSession` is a network round trip to Salesforce, and a hung one must not wedge
 * whatever is waiting on it — logout, an account handover, or the end of a conversation.
 * Past the deadline the clear is treated as failed, which is the safe reading for every
 * caller: nothing that depends on the session being gone gets to assume it is.
 */
const CLEAR_SESSION_TIMEOUT_MS = 2000;

/**
 * Makes the widget let go of its verified session, and resolves whether it did.
 *
 * `shouldEndSession` is forwarded straight to the client and decides whether a REST call
 * happens at all:
 *
 *  - `true` has the widget call its own `endSession` —
 *    `DELETE /iamessage/v1/conversation/{id}/session`. For a live session: logout, and an
 *    account handover.
 *  - `false` clears only the state it holds in this document, making no request. For a
 *    conversation that is already closed, where `endSession` would be a `403` on
 *    something that no longer exists.
 *
 * Never rejects, and never touches the verified-for marker: what a failed clear means for
 * the marker differs by caller, so that decision stays with them.
 */
export const clearSaltclickSession = async (shouldEndSession: boolean): Promise<boolean> => {
  const verificationApi = window.embeddedservice_bootstrap?.userVerificationAPI;
  if (!verificationApi) return true;

  // Before the call, not after: clearing returns the widget to its initial state, which is
  // indistinguishable from the user pressing End chat and is what both end-signal nets
  // watch for. Every clear here is the portal's own doing, so none of them is an end.
  muteSaltclickEndSignals();

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      // `clearSession` is typed `void | Promise<unknown>`; wrapping makes the
      // synchronous-return deployment resolve immediately rather than hang.
      Promise.resolve(verificationApi.clearSession(shouldEndSession)),
      new Promise((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`clearSession did not settle in ${CLEAR_SESSION_TIMEOUT_MS}ms`)),
          CLEAR_SESSION_TIMEOUT_MS,
        );
      }),
    ]);
  } catch (err) {
    console.error(
      `[Saltclick] Could not clear the chat session (shouldEndSession ${shouldEndSession}): `,
      err,
    );

    return false;
  } finally {
    clearTimeout(timer);
  }

  return true;
};

/**
 * Ends the verified chat session on logout, and resolves `true` when the session is
 * known to be gone.
 *
 * Nothing else takes it down: the widget outlives the React tree, so after logout the tab
 * still holds a chat verified as the account that just left. Waiting until the next login
 * to clear it is not enough — that only fires for a *different* account, so the same user
 * logging back in would reopen the previous conversation. GOTU-5918.
 *
 * It does not retire the conversation, despite the name: this ends a messaging *session*,
 * and Salesforce leaves the conversation open and resumable. What makes the next chat a
 * new conversation is `closeOpenSaltclickConversations`, which is driven by the user
 * ending a chat rather than by logout — a logged-out user has not ended their chat, and
 * logging back in should land them in it.
 *
 * Safe when there is nothing to end: `userVerificationAPI` exists only on a deployment
 * with User Verification enabled, and this never rejects the caller — logout must not
 * fail because Salesforce did.
 */
export const endSaltclickChatSession = async (): Promise<boolean> => {
  if (!window.embeddedservice_bootstrap?.userVerificationAPI) {
    // No widget, or a deployment without User Verification: there is no session for the
    // marker to describe, so it goes either way.
    setSaltclickVerifiedForUserId(undefined);

    return true;
  }

  if (!(await clearSaltclickSession(true))) {
    // The marker is deliberately kept. It is the only record that the widget still holds
    // someone else's verified conversation, so dropping it here would send the next login
    // down the plain verify path — handing a fresh token to a live conversation, which is
    // the carry-over GOTU-5918 exists to close.
    return false;
  }

  setSaltclickVerifiedForUserId(undefined);

  return true;
};
